import {
    BluethermBLEAPI,
    BluetoothDevice,
    BluetoothDevices,
    CooperAtkinsAPI,
    NordicAPI,
    TempAlertAPI,
    TestoAPI
} from '../../backbone/shared/models/BluetoothDeviceAPI';
import { CordovaPlugins } from '../../shared/CordovaPlugins';
import $f from '../shared/forms';
import { getAndroidVersion, isAndroid, isWeb, isWindows } from '../../shared/utils/is';
import { showErrorMessage, toastNotification } from '../../shared/utils/notifications';

export const BluetoothErrorCode = {
    ERROR_BLUETOOTH_DISABLED: 1,
    ERROR_NO_PERMISSION: 2,
    ERROR_INITIALIZE_BLUETOOTH: 3,
    ERROR_SCAN_FAILURE: 4,
    ERROR_SCAN_TIMEOUT: 5,
    ERROR_MISSING_ACTIVE_ADDRESS: 6,
    ERROR_FAILURE_TO_CONNECT: 7,
    ERROR_DISCONNECTION_SUCCESSFUL: 8,
    ERROR_FAILURE_TO_POLL: 9,
    ERROR_FAILURE_TO_STOP_POLL: 10,
    ERROR_PLATFORM_NOT_SUPPORTED: 11,
    ERROR_PAIR_REQUEST_DENIED: 12
};

export const getErrorMessageFromCode = (code) => {
    const androidPermissionMessage =
        getAndroidVersion() < 12
            ? _t('mobile_message_blue_therm_no_permission_android_pre_12')
            : _t('mobile_message_blue_therm_no_permission_android');

    const mapping = {
        [BluetoothErrorCode.ERROR_BLUETOOTH_DISABLED]: _t('mobile_message_blue_therm_error_bluetooth'),
        [BluetoothErrorCode.ERROR_SCAN_FAILURE]: _t('mobile_message_blue_therm_error_device'),
        [BluetoothErrorCode.ERROR_NO_PERMISSION]: isAndroid()
            ? androidPermissionMessage
            : _t('mobile_message_blue_therm_error_bluetooth') // iOS throws ERROR_BLUETOOTH_DISABLED instead so this should never happen
    };

    const defaultError = `${_t('mobile_message_blue_therm_error_code')} ${_t(
        'mobile_message_blue_therm_error_bluetooth'
    )}`;

    return mapping[code] || defaultError;
};

const checkBluetoothLocationPermission = () => {
    return new Promise((resolve) => {
        CordovaPlugins().bluetoothle.hasPermission((result) => {
            resolve(Boolean(result?.hasPermission));
        });
    });
};

const checkScanPermission = () => {
    return new Promise((resolve) => {
        CordovaPlugins().bluetoothle.hasPermissionBtScan((result) => {
            resolve(Boolean(result?.hasPermission));
        });
    });
};

const checkConnectPermission = () => {
    return new Promise((resolve) => {
        CordovaPlugins().bluetoothle.hasPermissionBtConnect((result) => {
            resolve(Boolean(result?.hasPermission));
        });
    });
};

const requestBluetoothLocationPermission = () => {
    return new Promise((resolve) => {
        CordovaPlugins().bluetoothle.requestPermission(
            (result) => {
                resolve(Boolean(result?.requestPermission));
            },
            () => {
                resolve(false);
            }
        );
    });
};

const requestScanPermission = () => {
    return new Promise((resolve) => {
        CordovaPlugins().bluetoothle.requestPermissionBtScan(
            (result) => {
                resolve(Boolean(result?.requestPermission));
            },
            () => {
                resolve(false);
            }
        );
    });
};

const requestConnectPermission = () => {
    return new Promise((resolve) => {
        CordovaPlugins().bluetoothle.requestPermissionBtConnect(
            (result) => {
                resolve(Boolean(result?.requestPermission));
            },
            () => {
                resolve(false);
            }
        );
    });
};

export const requestBluetoothPermissions = async () => {
    if (!isAndroid()) {
        return true;
    }

    if (getAndroidVersion() < 12) {
        const hasLocationPermission = await checkBluetoothLocationPermission();

        if (!hasLocationPermission) {
            const locationPermissionGiven = await requestBluetoothLocationPermission();

            if (!locationPermissionGiven) {
                return false;
            }
        }
        return true;
    }

    const hasScanPermission = await checkScanPermission();

    if (!hasScanPermission) {
        const scanPermissionGiven = await requestScanPermission();

        if (!scanPermissionGiven) {
            return false;
        }
    }

    const hasConnectPermission = await checkConnectPermission();

    if (!hasConnectPermission) {
        const connectPermissionGiven = await requestConnectPermission();

        if (!connectPermissionGiven) {
            return false;
        }
    }

    return true;
};

/**
 * Our Bluetooth integrations API and device manager.
 *
 * @see BluetoothDeviceAPI and subclasses
 */
const bluetooth = {
    SCAN_TIMEOUT_MS: 15000, // 15 seconds

    // We filter devices by name because we can't reliably get manufacturer data from the advertisement package on
    // Bluetherm. Also, this package is inconsistent between Android/iOS (due to bluetoothle plugin?)
    ALLOWED_DEVICE_NAMES: {
        ThermaQ: 'bluetherm_ble',
        Thermapen: 'bluetherm_ble',
        ThermapenBlue: 'bluetherm_ble',
        'BlueTherm One': 'bluetherm_ble',
        RayTemp: 'bluetherm_ble',
        Blue2: 'cooperatkins',
        TempAlert: 'tempalert',
        '104IR': 'testo',
        'TempTest Blue': 'bluetherm_ble',
        'Cooper-Atkins': 'nordic'
    },

    devices: null,
    device: null,
    api_by_type: {},
    callbacks: null,
    is_scanning: false,

    is_connected: false,
    is_connecting: false,

    _scan_timeout_id: null,

    initialize: function (options) {
        if (!this.supports_bluetooth()) {
            return;
        }

        var this_ = this;

        this._callback_wrappers = {
            on_pre_connect: function () {
                this_.callbacks.on_pre_connect(arguments);
                $f.application.vent.trigger('btdevice:connecting');
            },

            on_device_connected: function () {
                if (this_.is_connected) {
                    return;
                }

                this_.is_connected = true;
                this_.is_connecting = false;

                $f.application.vent.trigger('btdevice:connected');
                this_.callbacks.on_device_connected(arguments);
            },

            on_device_connection_failure: function (error_code) {
                if (!this_.is_connecting) {
                    return;
                }

                this_.is_connecting = false;
                this_.callbacks.on_device_connection_failure(error_code);
                if (isAndroid()) {
                    // @jira:IOT-1370 needed to fix issues on Android after connection drops unexpectedly
                    $f.mobile.bluetooth.close();
                }
                $f.application.vent.trigger('btdevice:disconnected');
            },

            on_device_disconnected: function () {
                if (!this_.is_connected) {
                    return;
                }

                this_.is_connected = false;
                this_.is_connecting = false;

                this_.callbacks.on_device_disconnected(arguments);
                if (isAndroid()) {
                    // @jira:IOT-1370 needed to fix issues on Android after connection drops unexpectedly
                    $f.mobile.bluetooth.close();
                }
                $f.application.vent.trigger('btdevice:disconnected');
            },

            on_reading_received: function (value) {
                this_.callbacks.on_reading_received(arguments);
                $f.application.vent.trigger('btdevice:new_reading', value);
            },

            on_button_pressed: function (value) {
                this_.callbacks.on_button_pressed(arguments);
                $f.application.vent.trigger('btdevice:button_pressed', value);
            }
        };

        this.api_by_type = {
            cooperatkins: new CooperAtkinsAPI(this._callback_wrappers),
            bluetherm_ble: new BluethermBLEAPI(this._callback_wrappers),
            nordic: new NordicAPI(this._callback_wrappers),
            tempalert: new TempAlertAPI(this._callback_wrappers),
            testo: new TestoAPI(this._callback_wrappers)
        };

        this.devices = new BluetoothDevices();
    },

    supports_bluetooth: function () {
        return !isWeb() && !isWindows();
    },

    register_callbacks: function (callbacks) {
        this.callbacks = callbacks;
    },

    unregister_callbacks: function () {
        this.callbacks = {
            on_pre_connect: function () {},

            on_device_connected: function () {},

            on_device_connection_failure: function (error_code) {},

            on_device_disconnected: function () {},

            on_reading_received: function (value) {},

            on_button_pressed: function (value) {}
        };
    },

    is_device_connected: function (callback) {
        if (!this.device) {
            callback(false);
            return;
        }

        this._get_device_api().is_device_connected(callback);
    },

    _register_device: function (device) {
        var existing_device = this.devices.findWhere({ address: device.get('address') });

        if (existing_device) {
            return existing_device;
        }

        this.devices.add(device);
        return device;
    },

    _get_device_api: function (device) {
        device = device || this.device;

        if (!device) {
            return null;
        }

        return this.api_by_type[device.get('type')];
    },

    _connect_to_device: function (device) {
        this.device = device;
        this._get_device_api().connect(device.get('address'));
    },

    _scan_and_connect: function () {
        var this_ = this;
        this.start_scan({
            on_device_discovered: function (device) {
                this_.stop_scan(function () {
                    this_._connect_to_device(device);
                });
            },
            on_timeout: function () {
                this_.stop_scan(function () {
                    this_._callback_wrappers.on_device_connection_failure();
                    $f.application.vent.trigger('btdevice:disconnected');
                });
            },
            error: function (errorCode) {
                this_.stop_scan(function () {
                    this_._callback_wrappers.on_device_connection_failure(errorCode);
                    $f.application.vent.trigger('btdevice:disconnected');
                });
            }
        });
    },

    connect: function () {
        if (this.is_connected || this.is_connecting) {
            return;
        }

        // if there's a preferred device, try to connect to it
        this.is_connected = false;
        this.is_connecting = true;
        if (this.devices.selected_device) {
            this._connect_to_device(this.devices.selected_device);
            return;
        }

        this._scan_and_connect();
    },

    disconnect: function () {
        // we haven't attempted to actually connect yet, so don't bother asking API to disconnect
        if (this.is_connecting && this.is_scanning) {
            this.is_connecting = false;
            $f.application.vent.trigger('btdevice:disconnected');
            this.stop_scan();
            return;
        }

        if (!this.device) {
            return;
        }

        this._get_device_api().disconnect();
        this.device = null;
    },

    close: function () {
        if (!this.device) {
            return;
        }

        CordovaPlugins().bluetoothle.close(null, null, {
            address: this.device.get('address')
        });
        this.device = null;
    },

    enumerate_devices: function () {
        var this_ = this;
        var detected_devices = [];

        this.start_scan({
            on_device_discovered: function (device) {
                var existing_device = this_._register_device(device);
                if (detected_devices.indexOf(existing_device) === -1) {
                    $f.application.vent.trigger('btdevice:device_discovered', existing_device);
                    detected_devices.push(existing_device);
                }
            },

            on_timeout: function () {
                $f.application.vent.trigger('btdevice:ble_scan_stopped');
                this_.is_scanning = false;
            },

            error: function (errorCode) {
                $f.application.vent.trigger('btdevice:ble_scan_stopped');
                this_.is_scanning = false;

                showErrorMessage(getErrorMessageFromCode(errorCode));
            }
        });
    },

    get_device_from_scan_response: function (response) {
        // localName is preferred, as that can't be changed by user, but it doesn't exist on Android,
        // and response.name seems to be the (unchangeable) local name on Android anyway.
        var name = response.name;
        if (response.advertisement && response.advertisement.localName) {
            name = response.advertisement.localName;
        }

        if (!name) {
            return;
        }

        var found_type = null;

        _.each(this.ALLOWED_DEVICE_NAMES, function (allowed_type, allowed_name) {
            if (name.toLowerCase().indexOf(allowed_name.toLowerCase()) === -1) {
                return true; // continue
            }

            found_type = allowed_type;
        });

        if (!found_type) {
            return;
        }

        return new BluetoothDevice({
            name: name,
            address: response.address,
            type: found_type
        });
    },

    start_scan: async function (options = {}) {
        options.on_device_discovered = options.on_device_discovered || function () {};
        options.error = options.error || function () {};

        const hasPermissions = await requestBluetoothPermissions();

        if (!hasPermissions) {
            options.error(BluetoothErrorCode.ERROR_NO_PERMISSION);
            return;
        }

        $f.application.vent.trigger('btdevice:ble_scan_started');
        this.is_scanning = true;

        // on_device_discovered can potentially stop the scan immediately, so handle that here
        if (!this.is_scanning) {
            return;
        }

        this.start_scan_ble(options);
    },

    /**
     * Check OS compatibility with BLE, permissions (on Android 6+), etc.
     */
    preflight: function (success = () => {}, error = () => {}) {
        var _has_run_initialize_callback = false;

        CordovaPlugins().bluetoothle.initialize(
            function () {
                // this bluetoothle.initialize() callback may be recalled if bluetooth starts/stops during the
                // connection, so we will prevent it from running multiple times. However, we do want the
                // callback to re-run if a (new) device is (re)connect, so _has_run_initialize_callback is not global.
                if (_has_run_initialize_callback) {
                    return;
                }
                _has_run_initialize_callback = true;

                // initialize() needs to be called in order for isEnabled() to work
                CordovaPlugins().bluetoothle.isEnabled(function (_response) {
                    if (!_response.isEnabled) {
                        return error(BluetoothErrorCode.ERROR_BLUETOOTH_DISABLED);
                    }

                    success();
                });
            },
            {
                request: true // user should be prompted to enable BT
            }
        );
    },

    _clear_scan_timeout: function () {
        // Clear any pending timeout
        if (this._scan_timeout_id) {
            clearTimeout(this._scan_timeout_id);
            this._scan_timeout_id = null;
        }
    },

    /**
     * This will scan for any applicable devices and report them as they are discovered
     * with the on_device_discovered() callback.
     */
    start_scan_ble: function (options) {
        options = options || {};
        options.error = options.error || function () {};
        options.on_timeout = options.on_timeout || function () {};
        options.on_device_discovered = options.on_device_discovered || function () {};

        var this_ = this;

        this.preflight(
            function () {
                // Set a timeout that will stop us from scanning indefinitely
                var continue_scanning = true;
                var has_finished_scanning = false;

                this_._clear_scan_timeout();

                this_._scan_timeout_id = setTimeout(function () {
                    continue_scanning = false;
                    if (has_finished_scanning) {
                        return;
                    }

                    options.on_timeout();

                    this_.stop_scan(function () {
                        has_finished_scanning = true;
                    });
                }, this_.SCAN_TIMEOUT_MS);

                var _error = options.error;
                options.error = function () {
                    has_finished_scanning = true;
                    _error.apply(this, arguments);
                };

                this_.is_scanning = true;

                $f.application.vent.trigger('btdevice:connecting');
                toastNotification(_t('mobile_message_bluetooth_device_press_button_field'));

                CordovaPlugins().bluetoothle.startScan(
                    function (response) {
                        if (!continue_scanning) {
                            return;
                        }

                        if (response.status !== 'scanResult') {
                            return;
                        }

                        var device = this_.get_device_from_scan_response(response);

                        if (!device) {
                            return;
                        }

                        options.on_device_discovered(device);
                    },
                    function (error) {
                        this_.is_scanning = false;

                        if (error.error === 'enable') {
                            return options.error(BluetoothErrorCode.ERROR_BLUETOOTH_DISABLED);
                        }

                        options.error(BluetoothErrorCode.ERROR_SCAN_FAILURE);
                    },
                    {
                        // @author:david even though CooperAtkins has specific services we can search for ('180F', '180D'),
                        // BluethermBLE does not, so we will scan for everything and filter out results by name manually.
                        services: []
                    }
                );
            },
            function (error_code, message) {
                options.error(error_code, message);
            }
        );
    },

    stop_scan: function (callback) {
        callback = callback || function () {};
        var this_ = this;

        this._clear_scan_timeout();

        CordovaPlugins().bluetoothle.stopScan(
            function (response) {
                this_.is_scanning = false;
                $f.application.vent.trigger('btdevice:ble_scan_stopped');
                callback();
            },
            function (error) {
                this_.is_scanning = false;
                $f.application.vent.trigger('btdevice:ble_scan_stopped');
                callback();
            }
        );
    },

    start_polling: function (options) {
        this._get_device_api().start_polling(options);
    },

    stop_polling: function () {
        this._get_device_api().stop_polling();
    }
};

export default bluetooth;
